/**
 * The Dink-owned CRM fields in plain words (DNKADM-0499; the field set is DNKAPI-0716 U1).
 *
 * The page's table reads its rows from `GET /v2/admin/crm` and looks each one's meaning up
 * here by its CRM field NAME (the names are fixed and all start with "Dink "). A
 * "Dink Last <service>" field for a tracked service reads its own line. Before the server
 * lists any field (fields never set up) the table shows {@link DINK_FIXED_FIELD_NAMES}.
 */
import type { CrmField, CrmTrackedService } from '@/api/crm';

const MEANINGS: ReadonlyArray<readonly [string, string]> = [
  ['Dink First Visit', 'Date of their first visit'],
  ['Dink Last Visit', 'Date of their most recent visit'],
  ['Dink Total Visits', 'How many visits they’ve made'],
  ['Dink No-Shows', 'How many bookings they didn’t show up for'],
  ['Dink Last Service', 'The service of their most recent visit'],
  ['Dink Membership Status', 'Active, Paused, Inactive or None'],
  ['Dink Membership Plan', 'The membership plan they’re on, empty if none'],
  ['Dink Credits Remaining', 'How many credits they have left'],
  ['Dink Lifetime Spend', 'Everything they’ve paid, minus refunds'],
  [
    'Dink Has Purchased',
    'Yes once they buy a membership, package or credits. Paying for a single booking doesn’t count.',
  ],
  ['Dink Last Purchase', 'Date they last bought a membership, package or credits'],
];

const MEANING_BY_NAME = new Map<string, string>(MEANINGS);

/** The 11 fixed fields, in table order. */
export const DINK_FIXED_FIELD_NAMES: readonly string[] = MEANINGS.map(([name]) => name);

/** What counts as a visit, said once under the table. */
export const VISIT_DEFINITION =
  'A visit is a booking they showed up for, or a confirmed booking whose time has passed.';

/** The plain line for one field, or null when this page has none for it. */
export function fieldMeaning(name: string, tracked: readonly CrmTrackedService[]): string | null {
  const fixed = MEANING_BY_NAME.get(name);
  if (fixed !== undefined) return fixed;
  const service = tracked.find((t) => t.fieldName === name);
  if (service) return `Date of their most recent ${service.name}`;
  if (name.startsWith('Dink Last ')) return `Date of their most recent ${name.slice('Dink Last '.length)}`;
  return null;
}

/**
 * The table's rows: the server's list, or (when it lists none) the fixed fields plus one per
 * tracked service, none of them in the CRM yet.
 */
export function fieldRows(fields: readonly CrmField[], tracked: readonly CrmTrackedService[]): CrmField[] {
  if (fields.length > 0) return [...fields];
  const names = [...DINK_FIXED_FIELD_NAMES, ...tracked.map((t) => t.fieldName)];
  return names.map((name) => ({ code: '', name, ghlFieldKey: null, exists: false }));
}
